import { forwardRef } from "react";

import { OTPInput, type OTPInputProps, type RenderProps } from "input-otp";

import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * Root
 * -----------------------------------------------------------------------------------------------*/
type RootProps = Omit<OTPInputProps, "render" | "children"> &
  Omit<React.ComponentPropsWithoutRef<typeof OTPInput>, "children"> & {
    children: (slot: RenderProps) => React.ReactElement;
  };

const Root = forwardRef<React.ComponentRef<typeof OTPInput>, RootProps>(({ className, children, ...props }, ref) => {
  return (
    <OTPInput
      {...props}
      ref={ref}
      containerClassName={cn("gap-2.5 flex flex-row items-center", className)}
      render={slot => children(slot)}
    />
  );
});

Root.displayName = "OTPInput";

/* -------------------------------------------------------------------------------------------------
 * Slot
 * -----------------------------------------------------------------------------------------------*/
type SlotProps = RenderProps["slots"][number] & React.ComponentPropsWithoutRef<"div">;

const Slot = forwardRef<React.ComponentRef<"div">, SlotProps>(
  ({ char, hasFakeCaret, isActive, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          `h-12.5 w-12.5 relative flex items-center justify-center rounded-[12px] border border-primary-gray bg-primary-white transition-all duration-200`,
          isActive && "shadow-[var(--shadow-glow)]",
          className,
        )}
        {...props}
      >
        <span className={"font-medium text-base text-primary-dark-blue"}>{char}</span>
        {hasFakeCaret && (
          <div
            className={"bottom-3.75 pointer-events-none absolute left-1/2 flex w-full -translate-x-1/2 justify-center"}
          >
            <div className={"animate-caret-blink h-0.5 w-3.75 bg-primary-dark-gray"} />
          </div>
        )}
      </div>
    );
  },
);

Slot.displayName = "InputOTPSlot";

export { Root, Slot };
export type { RootProps, SlotProps };
